import React, { useEffect, useMemo, useState } from "react";
import { client } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast, Toaster } from "sonner";
import {
  UserPlus, MoreHorizontal, Shield, ShieldAlert, Clock, Mail, History, UserX, Pencil, LogOut,
} from "lucide-react";
import { EditNameDialog } from "@/components/EditNameDialog";

const ROLE_META = {
  manager: { label: "Manager", color: "#1E3A8A", bg: "#EAF0FE" },
  service_coordinator: { label: "Service Coordinator", color: "#96650A", bg: "#FFF6E5" },
  technician: { label: "Technician", color: "#136B36", bg: "#E9F7EE" },
};

const STATUS_META = {
  active:    { label: "Active",    color: "#136B36", bg: "#E9F7EE" },
  suspended: { label: "Suspended", color: "#B42318", bg: "#FDECEC" },
  revoked:   { label: "Revoked",   color: "#5B6472", bg: "#EEF1F5" },
  pending:   { label: "Pending",   color: "#96650A", bg: "#FFF6E5" },
  accepted:  { label: "Accepted",  color: "#136B36", bg: "#E9F7EE" },
  cancelled: { label: "Cancelled", color: "#5B6472", bg: "#EEF1F5" },
  expired:   { label: "Expired",   color: "#B42318", bg: "#FDECEC" },
};

function Chip({ meta, mono }) {
  if (!meta) return null;
  return (
    <span className={`bf-chip ${mono ? "mono" : ""}`}
      style={{ background: meta.bg, color: meta.color }}>
      <span className="bf-dot" style={{ background: meta.color }} />
      {meta.label}
    </span>
  );
}

function userRoles(record) {
  const roles = record?.roles || [record?.role].filter(Boolean);
  return roles.map(role => ({ dispatcher: "service_coordinator", call_taker: "service_coordinator" }[role] || role));
}

function RoleChips({ roles }) {
  return (
    <div className="flex flex-wrap gap-1" data-testid="role-chip-list">
      {userRoles({ roles }).map(role => <Chip key={role} meta={ROLE_META[role]} />)}
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      timeZone: "America/Barbados",
      day: "2-digit", month: "short", year: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    });
  } catch { return iso; }
}

export default function UsersAccess() {
  const { user, refresh } = useAuth();
  const isOwner = user?.owner;
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [sites, setSites] = useState([]);
  const [audits, setAudits] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [confirm, setConfirm] = useState(null); // { title, warning, email, onConfirm }
  const [auditFilter, setAuditFilter] = useState({ action: "", email: "" });
  const [nameEdit, setNameEdit] = useState(null);
  const [signinUserId, setSigninUserId] = useState(null);
  const [signinRows, setSigninRows] = useState([]);

  const load = async () => {
    const [u, i, s] = await Promise.all([
      client.get("/users"), client.get("/invites"), client.get("/sites"),
    ]);
    setUsers(u.data); setInvites(i.data); setSites(s.data);
    if (isOwner) {
      const q = {};
      if (auditFilter.action) q.action = auditFilter.action;
      if (auditFilter.email) q.target_email = auditFilter.email;
      const a = await client.get("/audit", { params: q });
      setAudits(a.data);
    }
  };
  useEffect(() => { load(); }, [auditFilter.action, auditFilter.email, isOwner]);

  const activeManagerCount = users.filter(
    u => userRoles(u).includes("manager") && !["suspended", "revoked"].includes(u.status),
  ).length;

  const changeUserRoles = async (u, roles) => {
    try { await client.patch(`/users/${u.user_id}`, { roles }); toast.success(`Roles updated for ${u.email}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const changeUserStatus = async (u, status) => {
    try { await client.patch(`/users/${u.user_id}`, { status }); toast.success(`${status === "suspended" ? "Suspended" : "Reactivated"} ${u.email}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const revokeUser = async (u) => {
    try { await client.delete(`/users/${u.user_id}`); toast.success(`Revoked ${u.email}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const resendUser = async (u) => {
    try { await client.post(`/users/${u.user_id}/resend`); toast.success(`Sign-in link resent to ${u.email}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const signOutEverywhere = (u) => setConfirm({
    title: `Sign ${u.email} out everywhere?`,
    warning: "Every device where this person is signed in is signed out now. They can sign in again with Google.",
    email: u.email,
    onConfirm: async () => {
      try {
        const r = await client.post(`/users/${u.user_id}/sign-out-everywhere`);
        toast.success(`Signed out ${u.email} on ${r.data.sessions_ended} device(s)`);
        if (u.user_id === user?.user_id) window.location.href = "/login";
      } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    },
    testid: "confirm-signout-everywhere",
  });

  const changeInviteRoles = async (inv, roles) => {
    try { await client.patch(`/invites/${inv.id}`, { roles }); toast.success("Invite roles updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const resendInvite = async (inv) => {
    try { await client.post(`/invites/${inv.id}/resend`); toast.success(`Invite re-sent to ${inv.email}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const cancelInvite = async (inv) => {
    try { await client.delete(`/invites/${inv.id}`); toast.success("Invite cancelled"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openSignins = async (u) => {
    setSigninUserId(u.user_id);
    try { const r = await client.get(`/signin-history/${u.user_id}`); setSigninRows(r.data); }
    catch { setSigninRows([]); }
  };

  return (
    <div data-testid="users-page">
      <Toaster richColors position="top-right" />
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="mono text-[11px] uppercase tracking-widest" style={{ color: "#5B6472" }}>Administration</div>
          <h1 className="text-[28px] font-bold" style={{ color: "#12161B", letterSpacing: "-0.022em", marginTop: "6px" }}>Users &amp; Access</h1>
          <div className="text-[13.5px] mt-1" style={{ color: "#5B6472", lineHeight: 1.5 }}>
            Invite teammates, change roles, and revoke access. Every change is written to the audit log.
          </div>
        </div>
        <Button className="h-9 px-4 bf-btn-primary"
          onClick={() => setInviteOpen(true)} data-testid="invite-btn">
          <UserPlus size={14} className="mr-2" /> Invite user
        </Button>
      </div>

      {/* Pending Invites */}
      <section className="mt-7 bg-white rounded-[14px] overflow-hidden" style={{ boxShadow: "0 1px 2px rgba(16,24,32,.06), 0 6px 18px rgba(16,24,32,.045)" }} data-testid="invites-table">
        <div className="px-4 py-3.5 flex items-center justify-between border-b" style={{ borderColor: "#EFF1F4" }}>
          <div className="flex items-center gap-2">
            <Clock size={14} color="#96650A" />
            <div className="font-condensed uppercase tracking-wider text-[12px] font-semibold" style={{ color: "#5B6472" }}>Pending Invites</div>
          </div>
          <div className="mono text-[12px]" style={{ color: "#5B6472" }}>{invites.filter(i => i.status === "pending").length}</div>
        </div>
        <table className="w-full text-[13.5px]">
          <thead>
            <tr style={{ background: "#F7F8FA" }}>
              <th className="th-condensed text-left px-4 py-2">Email</th>
              <th className="th-condensed text-left px-3 py-2">Name</th>
              <th className="th-condensed text-left px-3 py-2">Role</th>
              <th className="th-condensed text-left px-3 py-2">Invited by</th>
              <th className="th-condensed text-left px-3 py-2">Date sent</th>
              <th className="th-condensed text-left px-3 py-2">Status</th>
              <th className="th-condensed text-right px-3 py-2 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {invites.length === 0 && (
              <tr><td colSpan={7} className="text-center py-8 text-[13px]" style={{ color: "#94A3B8" }}>No invites yet.</td></tr>
            )}
            {invites.map(inv => (
              <tr key={inv.id} className="border-t" style={{ borderColor: "#EFF1F4" }}
                data-testid={`invite-row-${inv.email}`}
              >
                <td className="px-4 py-3 mono text-[12px]">{inv.email}</td>
                <td className="px-3 py-3">{inv.name || <span style={{ color: "#8A93A1" }}>—</span>}</td>
                <td className="px-3 py-3"><RoleChips roles={userRoles(inv)} /></td>
                <td className="px-3 py-3 mono text-[12px]" style={{ color: "#5B6472" }}>{inv.invited_by}</td>
                <td className="px-3 py-3 mono text-[12px]" style={{ color: "#5B6472" }}>{fmtDate(inv.last_sent_at || inv.created_at)}</td>
                <td className="px-3 py-3"><Chip meta={STATUS_META[inv.status]} /></td>
                <td className="px-3 py-3 text-right">
                  {(inv.status === "pending" || inv.status === "expired") && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="p-1 rounded hover:bg-slate-100" data-testid={`invite-menu-${inv.email}`}>
                          <MoreHorizontal size={16} />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => resendInvite(inv)} data-testid={`invite-resend-${inv.email}`}>
                          <Mail size={14} className="mr-2" />
                          {inv.status === "expired" ? "Resend & reactivate" : "Resend email"}
                        </DropdownMenuItem>
                        {inv.status === "pending" && (<>
                          <DropdownMenuSeparator />
                          <div className="px-2 py-1.5 text-[11px]" style={{ color: "#5F6B7A" }}>Roles</div>
                          {Object.keys(ROLE_META).map(r => (
                            <DropdownMenuItem
                              key={r}
                              onClick={() => {
                                const current = userRoles(inv);
                                const next = current.includes(r)
                                  ? current.filter(value => value !== r)
                                  : [...current, r];
                                if (next.length) changeInviteRoles(inv, next);
                              }}
                              data-testid={`invite-role-${inv.email}-${r}`}
                            >
                              {ROLE_META[r].label} {userRoles(inv).includes(r) ? "✓" : ""}
                            </DropdownMenuItem>
                          ))}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => cancelInvite(inv)}
                            data-testid={`invite-cancel-${inv.email}`}>
                            <span style={{ color: "#B91C1C" }}>Cancel invite</span>
                          </DropdownMenuItem>
                        </>)}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* Active users */}
      <section className="mt-6 bg-white rounded-[14px] overflow-hidden" style={{ boxShadow: "0 1px 2px rgba(16,24,32,.06), 0 6px 18px rgba(16,24,32,.045)" }} data-testid="users-table">
        <div className="px-4 py-3.5 flex items-center justify-between border-b" style={{ borderColor: "#EFF1F4" }}>
          <div className="flex items-center gap-2">
            <Shield size={14} color="#2563EB" />
            <div className="font-condensed uppercase tracking-wider text-[12px] font-semibold" style={{ color: "#5B6472" }}>Active Users</div>
          </div>
          <div className="mono text-[12px]" style={{ color: "#5B6472" }}>{users.length}</div>
        </div>
        <table className="w-full text-[13.5px]">
          <thead>
            <tr style={{ background: "#F7F8FA" }}>
              <th className="th-condensed text-left px-4 py-2">Email</th>
              <th className="th-condensed text-left px-3 py-2">Name</th>
              <th className="th-condensed text-left px-3 py-2">Role</th>
              <th className="th-condensed text-left px-3 py-2">Status</th>
              <th className="th-condensed text-left px-3 py-2">Invited by</th>
              <th className="th-condensed text-left px-3 py-2">Invite date</th>
              <th className="th-condensed text-left px-3 py-2">Last sign-in</th>
              <th className="th-condensed text-right px-3 py-2 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.user_id} className="border-t"
                style={{ borderColor: "#EFF1F4" }}
                data-testid={`user-row-${u.email}`}
              >
                <td className="px-4 py-3 mono text-[12px]">
                  {u.email}
                  {u.owner && <Badge className="ml-2 h-4 text-[9px] px-1.5 rounded-full" style={{ background: "#EAF0FE", color: "#1E3A8A" }}>OWNER</Badge>}
                </td>
                <td className="px-3 py-3">
                  {u.name || <span style={{ color: "#8A93A1" }}>—</span>}
                  <button className="ml-1.5 p-1 rounded hover:bg-slate-100 align-middle" title="Edit name"
                          onClick={() => setNameEdit(u)} data-testid={`user-edit-name-${u.email}`}>
                    <Pencil size={12} color="#5B6472" />
                  </button>
                </td>
                <td className="px-3 py-3"><RoleChips roles={userRoles(u)} /></td>
                <td className="px-3 py-3"><Chip meta={STATUS_META[u.status || "active"]} /></td>
                <td className="px-3 py-3 mono text-[12px]" style={{ color: "#5B6472" }}>{u.invited_by || "—"}</td>
                <td className="px-3 py-3 mono text-[12px]" style={{ color: "#5B6472" }}>{fmtDate(u.invite_date || u.created_at)}</td>
                <td className="px-3 py-3 mono text-[12px]" style={{ color: "#5B6472" }}>{fmtDate(u.last_sign_in)}</td>
                <td className="px-3 py-3 text-right">
                  {isOwner && (u.user_id === user?.user_id || u.owner) && (
                    <Button variant="outline" className="h-8 text-[12px] bf-btn-ghost" onClick={() => signOutEverywhere(u)}
                      data-testid={`user-signout-everywhere-${u.email}`}>
                      <LogOut size={12} className="mr-1" /> Sign out everywhere
                    </Button>
                  )}
                  {u.user_id !== user?.user_id && !u.owner && u.status === "revoked" && (
                    <Button variant="outline" className="h-8 text-[12px] bf-btn-ghost"
                      onClick={async () => {
                        try {
                          await client.post(`/users/${u.user_id}/reinstate`);
                          toast.success(`Reinstated ${u.email} — fresh invite sent`);
                          load();
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Reinstate failed");
                        }
                      }}
                      data-testid={`user-reinstate-${u.email}`}
                    >
                      <Mail size={12} className="mr-1" /> Reinstate
                    </Button>
                  )}
                  {u.user_id !== user?.user_id && !u.owner && u.status !== "revoked" && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="p-1 rounded hover:bg-slate-100" data-testid={`user-menu-${u.email}`}>
                          <MoreHorizontal size={16} />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <div className="px-2 py-1.5 text-[11px]" style={{ color: "#5F6B7A" }}>Roles</div>
                        {Object.keys(ROLE_META).map(r => (
                          <DropdownMenuItem key={r} onClick={() => {
                            const current = userRoles(u);
                            const next = current.includes(r)
                              ? current.filter(value => value !== r)
                              : [...current, r];
                            if (next.length) changeUserRoles(u, next);
                          }}
                            data-testid={`user-role-${u.email}-${r}`}
                            disabled={
                              (userRoles(u).includes("manager") && r === "manager" && activeManagerCount <= 1)
                              || (u.owner && r === "manager")
                            }
                          >
                            {ROLE_META[r].label} {userRoles(u).includes(r) ? "✓" : ""}
                          </DropdownMenuItem>
                        ))}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => resendUser(u)}>
                          <Mail size={14} className="mr-2" /> Resend sign-in link
                        </DropdownMenuItem>
                        {isOwner && (
                          <DropdownMenuItem onClick={() => openSignins(u)}>
                            <History size={14} className="mr-2" /> Sign-in history
                          </DropdownMenuItem>
                        )}
                        {isOwner && (
                          <DropdownMenuItem onClick={() => signOutEverywhere(u)}
                            data-testid={`user-signout-everywhere-${u.email}`}>
                            <LogOut size={14} className="mr-2" /> Sign out everywhere
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        {u.status === "suspended" ? (
                          <DropdownMenuItem onClick={() => changeUserStatus(u, "active")}
                            data-testid={`user-reactivate-${u.email}`}
                          >Reactivate</DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onClick={() => setConfirm({
                            title: `Suspend ${u.email}?`,
                            warning: "Their active session will end immediately. They will not be able to sign in until you reactivate them.",
                            email: u.email,
                            onConfirm: () => changeUserStatus(u, "suspended"),
                            testid: "confirm-suspend",
                          })}
                            data-testid={`user-suspend-${u.email}`}
                          >
                            <span style={{ color: "#B45309" }}>Suspend</span>
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => setConfirm({
                          title: `Revoke access for ${u.email}?`,
                          warning: "Their session ends immediately. Their audit trail is retained, and a fresh invite is required to return.",
                          email: u.email,
                          onConfirm: () => revokeUser(u),
                          testid: "confirm-revoke",
                        })}
                          data-testid={`user-revoke-${u.email}`}
                        >
                          <span style={{ color: "#B91C1C" }}><UserX size={13} className="inline mr-1 -mt-0.5" />Revoke access</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* Owner section */}
      {isOwner && (
        <section className="mt-8" data-testid="owner-section">
          <div className="flex items-center gap-2 mb-3">
            <ShieldAlert size={16} color="#B42318" />
            <div className="font-condensed uppercase tracking-wider text-[12px] font-semibold" style={{ color: "#5B6472" }}>
              Owner-only tools
            </div>
          </div>

          {/* Audit */}
          <CronStatus />
          <div className="bg-white rounded-[14px] overflow-hidden mt-6" style={{ boxShadow: "0 1px 2px rgba(16,24,32,.06), 0 6px 18px rgba(16,24,32,.045)" }} data-testid="audit-log">
            <div className="px-4 py-3.5 flex items-center justify-between border-b flex-wrap gap-2" style={{ borderColor: "#EFF1F4" }}>
              <div className="font-condensed uppercase tracking-wider text-[12px] font-semibold" style={{ color: "#5B6472" }}>Audit log</div>
              <div className="flex items-center gap-2">
                <Input placeholder="Filter by email" value={auditFilter.email}
                  onChange={e => setAuditFilter(f => ({ ...f, email: e.target.value }))}
                  className="h-9 w-48 mono text-[12px] rounded-[10px]" data-testid="audit-email-filter" />
                <Select value={auditFilter.action || "__all"} onValueChange={v => setAuditFilter(f => ({ ...f, action: v === "__all" ? "" : v }))}>
                  <SelectTrigger className="h-9 w-44 text-[12px] rounded-[10px]" data-testid="audit-action-filter"><SelectValue placeholder="All actions" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all">All actions</SelectItem>
                    {["invite_created","invite_resent","invite_updated","invite_cancelled",
                      "role_changed","status_changed","user_revoked","signin_link_resent",
                      "ownership_transferred","audit_exported","job_tech_paused","job_tech_resumed",
                      "job_times_edited","job_report_regenerated"].map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button variant="outline" className="h-9 text-[12px] bf-btn-ghost" onClick={async () => {
                  const q = {};
                  if (auditFilter.action) q.action = auditFilter.action;
                  if (auditFilter.email) q.target_email = auditFilter.email;
                  const r = await client.get("/audit/export", { params: q, responseType: "blob" });
                  const url = URL.createObjectURL(r.data);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `audit-${new Date().toISOString().slice(0,10)}.csv`;
                  document.body.appendChild(a); a.click(); a.remove();
                  URL.revokeObjectURL(url);
                  toast.success("Audit log exported");
                }} data-testid="audit-export-btn">Export CSV</Button>
                <Button variant="outline" className="h-9 text-[12px] bf-btn-ghost"
                  onClick={async () => {
                    try {
                      await client.post("/audit/digest/send-now");
                      toast.success("Digest queued — check your inbox shortly");
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Could not queue digest");
                    }
                  }}
                  data-testid="digest-send-now-btn"
                >
                  <Mail size={12} className="mr-1" /> Send digest now
                </Button>
              </div>
            </div>
            <table className="w-full text-[13px]">
              <thead>
                <tr style={{ background: "#F7F8FA" }}>
                  <th className="th-condensed text-left px-4 py-2">When</th>
                  <th className="th-condensed text-left px-3 py-2">Actor</th>
                  <th className="th-condensed text-left px-3 py-2">Action</th>
                  <th className="th-condensed text-left px-3 py-2">Target</th>
                  <th className="th-condensed text-left px-3 py-2">Details</th>
                </tr>
              </thead>
              <tbody>
                {audits.length === 0 && (
                  <tr><td colSpan={5} className="text-center py-8 text-[13px]" style={{ color: "#94A3B8" }}>No entries.</td></tr>
                )}
                {audits.map(a => (
                  <tr key={a.id} className="border-t" style={{ borderColor: "#EFF1F4" }}>
                    <td className="px-4 py-2.5 mono text-[11px]" style={{ color: "#5B6472" }}>{fmtDate(a.ts)}</td>
                    <td className="px-3 py-2.5 mono text-[12px]">{a.actor_email}</td>
                    <td className="px-3 py-2.5 mono text-[11px]">{a.action}</td>
                    <td className="px-3 py-2.5 mono text-[12px]" style={{ color: "#5B6472" }}>{a.target_email || "—"}</td>
                    <td className="px-3 py-2.5 text-[12px]" style={{ color: "#5B6472" }}>
                      {a.details && Object.keys(a.details).length ? (
                        <code className="mono text-[11px]">{Object.entries(a.details).map(([k, v]) => `${k}=${v}`).join(" · ")}</code>
                      ) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <DangerZone users={users} onConfirm={setConfirm} onDone={load} />
        </section>
      )}

      {/* Invite slide-over */}
      <InviteSheet open={inviteOpen} onOpenChange={setInviteOpen} onDone={load} sites={sites} />
      <EditNameDialog person={nameEdit} onClose={() => setNameEdit(null)} onSaved={() => { if (nameEdit?.user_id === user?.user_id) refresh(); setNameEdit(null); load(); }} />

      {/* Destructive confirm */}
      {confirm && (
        <ConfirmEmailDialog
          open={!!confirm} data={confirm}
          onCancel={() => setConfirm(null)}
          onConfirm={async () => { await confirm.onConfirm(); setConfirm(null); }}
        />
      )}

      {/* Sign-in history dialog */}
      {signinUserId && (
        <Dialog open={true} onOpenChange={() => setSigninUserId(null)}>
          <DialogContent className="max-w-[560px]" data-testid="signin-history-dialog">
            <DialogHeader>
              <DialogTitle>Sign-in history</DialogTitle>
              <DialogDescription>Most recent sessions for this user.</DialogDescription>
            </DialogHeader>
            <div className="max-h-[400px] overflow-y-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr style={{ background: "#F7F8FA" }}>
                    <th className="th-condensed text-left px-3 py-2">When</th>
                    <th className="th-condensed text-left px-3 py-2">IP</th>
                    <th className="th-condensed text-left px-3 py-2">Agent</th>
                  </tr>
                </thead>
                <tbody>
                  {signinRows.length === 0 && <tr><td colSpan={3} className="text-center py-6" style={{ color: "#94A3B8" }}>No sign-ins recorded.</td></tr>}
                  {signinRows.map(s => (
                    <tr key={s.id} className="border-t" style={{ borderColor: "#EFF1F4" }}>
                      <td className="px-3 py-2 mono text-[11px]">{fmtDate(s.ts)}</td>
                      <td className="px-3 py-2 mono text-[11px]">{s.ip || "—"}</td>
                      <td className="px-3 py-2 text-[11px] truncate max-w-[260px]" title={s.user_agent}>{s.user_agent || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function InviteSheet({ open, onOpenChange, onDone, sites = [] }) {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [roles, setRoles] = useState(["technician"]);
  const [region, setRegion] = useState("");
  const [siteIds, setSiteIds] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) {
      setEmail("");
      setFirstName("");
      setLastName("");
      setRoles(["technician"]);
      setRegion("");
      setSiteIds([]);
    }
  }, [open]);

  const toggleSite = (id) =>
    setSiteIds(ids => ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]);

  const toggleRole = (role) => {
    setRoles(current => current.includes(role)
      ? current.length > 1 ? current.filter(value => value !== role) : current
      : [...current, role]);
  };

  const submit = async () => {
    if (!email.trim()) { toast.error("Email is required"); return; }
    if (!roles.length) { toast.error("Pick at least one role"); return; }
    try {
      setSending(true);
      const payload = { email, roles, region_restriction: region };
      if (firstName.trim() || lastName.trim()) { payload.first_name = firstName; payload.last_name = lastName; }
      if (roles.includes("technician")) payload.site_ids = siteIds;
      const r = await client.post("/invites", payload);
      if (r.data.email_sent === false) toast.warning("Invite created but email could not be sent");
      else toast.success(`Invite sent to ${email}`);
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create invite");
    } finally { setSending(false); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-[460px] p-0 overflow-y-auto" data-testid="invite-sheet">
        <div className="p-6 border-b" style={{ borderColor: "#EFF1F4" }}>
          <SheetHeader className="p-0 text-left">
            <SheetTitle className="text-xl font-semibold tracking-tight">Invite a user</SheetTitle>
            <SheetDescription className="text-[13.5px]" style={{ color: "#5B6472", lineHeight: 1.5 }}>
              They get a sign-in link by email. Their role is applied the moment they sign in with Google — no picker, no extra step.
            </SheetDescription>
          </SheetHeader>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <Label className="font-condensed uppercase tracking-wider text-[12px] font-semibold" style={{ color: "#5B6472" }}>
              Email <span style={{ color: "#B42318" }}>*</span>
            </Label>
            <Input className="mt-1 mono rounded-[10px]" placeholder="person@company.com" value={email}
              onChange={e => setEmail(e.target.value)} data-testid="invite-email" autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="font-condensed uppercase tracking-wider text-[12px] font-semibold" style={{ color: "#5B6472" }}>First name</Label>
              <Input className="mt-1 rounded-[10px]" placeholder="Optional" value={firstName}
                onChange={e => setFirstName(e.target.value)} data-testid="invite-first-name" />
            </div>
            <div>
              <Label className="font-condensed uppercase tracking-wider text-[12px] font-semibold" style={{ color: "#5B6472" }}>Last name</Label>
              <Input className="mt-1 rounded-[10px]" placeholder="Optional" value={lastName}
                onChange={e => setLastName(e.target.value)} data-testid="invite-last-name" />
            </div>
          </div>
          <fieldset>
            <legend className="font-condensed uppercase tracking-wider text-[12px] font-semibold" style={{ color: "#5B6472" }}>
              Roles <span style={{ color: "#B42318" }}>*</span>
            </legend>
            <div className="mt-2 grid gap-2">
              {Object.entries(ROLE_META).map(([role, meta]) => (
                <label key={role} className="flex items-center gap-2 text-[13.5px] cursor-pointer" data-testid={`invite-role-choice-${role}`}>
                  <input
                    type="checkbox"
                    checked={roles.includes(role)}
                    onChange={() => toggleRole(role)}
                    className="h-4 w-4 accent-[#2563EB]"
                    data-testid={`invite-role-checkbox-${role}`}
                  />
                  <Chip meta={meta} />
                </label>
              ))}
            </div>
          </fieldset>
          {roles.includes("technician") && (
            <>
              <div>
                <Label className="font-condensed uppercase tracking-wider text-[12px] font-semibold" style={{ color: "#5B6472" }}>
                  Sites this technician can work
                </Label>
                <div className="mt-1 rounded-[12px] p-2 max-h-[180px] overflow-y-auto space-y-1"
                  style={{ background: "#F1F3F6" }} data-testid="invite-sites">
                  {sites.length === 0 && <div className="text-[13px] px-1" style={{ color: "#8A93A1" }}>No sites configured.</div>}
                  {sites.map(s => (
                    <label key={s.id} className="flex items-center gap-2 text-[13.5px] px-1.5 py-1.5 rounded-[10px] hover:bg-white cursor-pointer"
                      data-testid={`invite-site-${s.id}`}>
                      <input type="checkbox" checked={siteIds.includes(s.id)}
                        onChange={() => toggleSite(s.id)}
                        className="h-4 w-4 accent-[#2563EB]" />
                      <span className="flex-1">{s.name}</span>
                      <span className="text-[11px]" style={{ color: "#8A93A1" }}>{s.address}</span>
                    </label>
                  ))}
                </div>
                <div className="text-[12px] mt-1" style={{ color: "#5B6472" }}>
                  Leave empty to allow all sites. Selected: {siteIds.length}.
                </div>
              </div>
              <div>
                <Label className="font-condensed uppercase tracking-wider text-[12px] font-semibold" style={{ color: "#5B6472" }}>
                  Region note (free text, optional)
                </Label>
                <Input className="mt-1 rounded-[10px]" placeholder="e.g. South Coast · Christ Church only" value={region}
                  onChange={e => setRegion(e.target.value)} data-testid="invite-region" />
              </div>
            </>
          )}
        </div>
        <div className="p-6 border-t flex items-center gap-2" style={{ borderColor: "#EFF1F4" }}>
          <Button className="bf-btn-primary px-4" onClick={submit}
            disabled={sending} data-testid="invite-submit">
            {sending ? "Sending…" : "Send invite"}
          </Button>
          <Button variant="outline" className="bf-btn-ghost" onClick={() => onOpenChange(false)} data-testid="invite-cancel">Cancel</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function ConfirmEmailDialog({ data, onConfirm, onCancel, open }) {
  const [typed, setTyped] = useState("");
  const matches = typed.trim().toLowerCase() === (data?.email || "").toLowerCase();
  return (
    <Dialog open={open} onOpenChange={v => !v && onCancel()}>
      <DialogContent className="max-w-[460px]" data-testid={data?.testid || "confirm-dialog"}>
        <DialogHeader>
          <DialogTitle>{data.title}</DialogTitle>
          <DialogDescription>{data.warning}</DialogDescription>
        </DialogHeader>
        <div className="mt-2">
          <Label className="text-[12.5px]" style={{ color: "#5B6472" }}>
            Type <code className="mono">{data.email}</code> to confirm.
          </Label>
          <Input className="mt-1 mono rounded-[10px]" value={typed} onChange={e => setTyped(e.target.value)}
            placeholder={data.email} data-testid="confirm-type-email" />
        </div>
        <DialogFooter className="mt-3">
          <Button variant="outline" className="bf-btn-ghost" onClick={onCancel}>Cancel</Button>
          <Button className="text-white rounded-[10px]" style={{ background: matches ? "#B42318" : "#94A3B8" }}
            disabled={!matches} onClick={onConfirm}
            data-testid="confirm-proceed-btn"
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CronStatus() {
  const [data, setData] = useState(null);
  const load = async () => {
    try { const r = await client.get("/cron/status"); setData(r.data); }
    catch { setData(null); }
  };
  useEffect(() => { load(); const i = setInterval(load, 30000); return () => clearInterval(i); }, []);
  if (!data) return null;
  const rows = [
    { key: "expire-invites", label: "Invite expiry", cadence: "Every 6 hours" },
    { key: "weekly-digest",  label: "Owner weekly digest", cadence: "Mon 07:00 AST (11:00 UTC)" },
  ];
  const fmt = (iso) => iso ? new Date(iso).toLocaleString("en-GB", { timeZone: "America/Barbados", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : "—";
  return (
    <section className="bg-white rounded-[14px] overflow-hidden" style={{ boxShadow: "0 1px 2px rgba(16,24,32,.06), 0 6px 18px rgba(16,24,32,.045)" }} data-testid="cron-status">
      <div className="px-4 py-3.5 border-b flex items-center justify-between" style={{ borderColor: "#EFF1F4" }}>
        <div className="flex items-center gap-2">
          <Clock size={14} color="#2563EB" />
          <div className="font-condensed uppercase tracking-wider text-[12px] font-semibold" style={{ color: "#5B6472" }}>Platform crons</div>
        </div>
        <button onClick={load} className="text-[11px] mono" style={{ color: "#2563EB" }} data-testid="cron-refresh">refresh</button>
      </div>
      <table className="w-full text-[13.5px]">
        <thead>
          <tr style={{ background: "#F7F8FA" }}>
            <th className="th-condensed text-left px-4 py-2">Job</th>
            <th className="th-condensed text-left px-3 py-2">Cadence</th>
            <th className="th-condensed text-left px-3 py-2">Last run</th>
            <th className="th-condensed text-left px-3 py-2">Outcome</th>
            <th className="th-condensed text-left px-3 py-2">Detail</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const run = data[r.key];
            const ok = run?.outcome === "ok";
            return (
              <tr key={r.key} className="border-t"
                style={{ borderColor: "#EFF1F4" }}
                data-testid={`cron-row-${r.key}`}
              >
                <td className="px-4 py-3 font-medium">{r.label}</td>
                <td className="px-3 py-3 mono text-[12px]" style={{ color: "#5B6472" }}>{r.cadence}</td>
                <td className="px-3 py-3 mono text-[12px]">{run ? fmt(run.ts) : <span style={{ color: "#8A93A1" }}>never</span>}</td>
                <td className="px-3 py-3">
                  {!run ? <Chip meta={{ label: "Pending", color: "#5B6472", bg: "#EEF1F5" }} /> :
                    ok ? <Chip meta={{ label: "OK", color: "#136B36", bg: "#E9F7EE" }} /> :
                         <Chip meta={{ label: "Error", color: "#B42318", bg: "#FDECEC" }} />}
                </td>
                <td className="px-3 py-3 text-[12px]" style={{ color: "#5B6472" }}>
                  {run?.outcome === "ok" && r.key === "expire-invites" && `${run.processed ?? 0} processed`}
                  {run?.outcome === "ok" && r.key === "weekly-digest" && `${run.rows ?? 0} rows → ${run.recipient || ""}`}
                  {run?.outcome === "error" && <span style={{ color: "#B42318" }}>{run.error}</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

function DangerZone({ users, onConfirm, onDone }) {
  const managers = users.filter(u => userRoles(u).includes("manager") && !u.owner);
  const [target, setTarget] = useState("");
  const doTransfer = async () => {
    try { await client.post("/owner/transfer", { email: target, confirm_email: target }); toast.success("Ownership transferred"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="mt-6 bf-card" style={{ padding: "20px", background: "#FFF9F9" }} data-testid="danger-zone">
      <div className="flex items-center gap-2">
        <ShieldAlert size={16} color="#B42318" />
        <div className="font-semibold text-[15px]" style={{ color: "#B42318" }}>Danger zone</div>
      </div>
      <div className="text-[13.5px] mt-1" style={{ color: "#5B6472", lineHeight: 1.5 }}>
        Transfer owner rights to another active Manager. You keep your Manager role but lose owner-only tools.
      </div>
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <Select value={target} onValueChange={setTarget}>
          <SelectTrigger className="h-9 w-[280px] rounded-[10px] bg-white" data-testid="transfer-target"><SelectValue placeholder="Pick a Manager" /></SelectTrigger>
          <SelectContent>
            {managers.length === 0 && <div className="px-2 py-1.5 text-[12px]" style={{ color: "#8A93A1" }}>No other Managers yet.</div>}
            {managers.map(m => <SelectItem key={m.user_id} value={m.email}>{m.email}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button className="text-white rounded-[10px]" style={{ background: "#B42318" }} disabled={!target}
          onClick={() => onConfirm({
            title: "Transfer ownership?",
            warning: "The new owner immediately gains access to the audit log and this danger zone. You will lose it.",
            email: target,
            onConfirm: doTransfer,
            testid: "confirm-transfer",
          })}
          data-testid="transfer-btn"
        >
          Transfer ownership
        </Button>
      </div>
    </div>
  );
}
